// LAP-550 V1.2 — Page détail "Evolution de mes investissements"
import React, { useEffect, useState, useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { makeGetRequest } from 'utils/api'
import DetailPageLayout from '../../_shared/DetailPageLayout'
import AssetTable from '../../_shared/AssetTable'
import { invTheme } from '../../_shared/theme'
import { dateCol, moneyCol, deltaCol, toNum } from '../../_shared/gridCols'

export default function EvolutionDetail() {
  const { t } = useTranslation('layout')
  const [report, setReport] = useState(null)
  const [history, setHistory] = useState([])
  const [view, setView] = useState('value')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const fetch = async () => {
      const { data } = await makeGetRequest('/api/investment/reports/', {}, true)
      const r = (data || []).find((x) => x.type === 'valeur-investment')
      setReport(r?.data || null)
      const h = await makeGetRequest('/api/investment/investment-history/', {}, true).catch(() => null)
      if (h?.data?.results) setHistory(h.data.results)
    }
    fetch()
  }, [])

  const chartData = useMemo(() => {
    if (!report) return null
    return {
      labels: report.labels || [],
      datasets: [
        {
          label: t('investments.portfolio.invValue.activePortfolio'),
          data: view === 'value' ? report.portfolio : report.profitability,
          borderColor: invTheme.accents.portfolio,
          backgroundColor: 'rgba(99,160,255,0.15)',
          tension: 0.35,
          fill: true,
          pointRadius: 3,
        },
      ],
    }
  }, [report, view, t])

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        datalabels: { display: false },
        legend: {
          labels: { color: 'rgba(255,255,255,0.7)', usePointStyle: true, font: { size: 11 } },
        },
      },
      scales: {
        x: {
          ticks: {
            color: 'rgba(255,255,255,0.55)',
            callback: (e) => moment(report?.labels?.[e]).format('MMM YY'),
          },
          grid: { color: 'rgba(255,255,255,0.05)' },
        },
        y: { ticks: { color: 'rgba(255,255,255,0.55)' }, grid: { color: 'rgba(255,255,255,0.06)' } },
      },
    }),
    [report],
  )

  // `date` était formatée dans le valueGetter : le tri portait alors sur une
  // chaîne. Elle est désormais typée date ; l'affichage reste localisé.
  const columns = useMemo(
    () => [
      dateCol({
        field: 'date',
        headerName: t('investments.detail.cols.date', 'Date'),
        width: 120,
        fallbackFields: ['snapshot_date'],
      }),
      moneyCol({
        field: 'total_value',
        headerName: t('investments.detail.cols.totalValue', 'Valeur totale'),
        width: 150,
        getter: (p) => toNum(p.row.total_value ?? p.row.value),
      }),
      moneyCol({ field: 'income', headerName: t('investments.detail.cols.income', 'Revenu'), width: 130 }),
      deltaCol({
        field: 'profitability',
        headerName: t('investments.detail.cols.profitability', 'Rentabilité'),
        width: 140,
      }),
    ],
    [t],
  )

  return (
    <DetailPageLayout
      title={t('investments.portfolio.invValue.title')}
      subtitle={t('investments.detail.evolutionSubtitle', 'Evolution de mes investissements')}
      icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
      accent={invTheme.accents.portfolio}
      chartSubtitleOptions={[
        { value: 'value', label: t('investments.detail.byValue', 'Valeur') },
        { value: 'profitability', label: t('investments.detail.byProfit', 'Rentabilité') },
      ]}
      chartSubtitle={view}
      onChartSubtitleChange={setView}
      sumLabel={t('investments.detail.totalSum', 'Total')}
      sumValue={report?.total !== undefined ? `${report.total} €` : '—'}
      chart={chartData ? <Line data={chartData} options={chartOptions} /> : null}
      tableTitle={t('investments.detail.historyTitle', 'Historique de mes investissements')}
      tableCount={history.length}
      tableContent={
        <AssetTable
          rows={history}
          columns={columns}
          emptyLabel={t('investments.portfolio.noInvestmentsYet')}
          getRowId={(r) => r.id ?? `${r.date || r.snapshot_date}-${r.value || ''}`}
          searchable
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('investments.detail.searchHistory', 'Rechercher une date, un montant…')}
        />
      }
    />
  )
}
